import React, { useState, useEffect } from 'react';
import { Calendar, Play, Pause, CheckCircle, Clock, Users, Trophy, ArrowRight, Save, Settings, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminRoundFieldsManager from './AdminRoundFieldsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRoundConfigSection = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('config'); // 'config' or 'fields'

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/rounds/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (err) {
      toast.error('Failed to fetch round configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateRound = async (roundNumber, updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/rounds/config`, {
        round_number: roundNumber,
        ...updates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Round ${roundNumber} updated`);
      fetchConfig();
    } catch (err) {
      toast.error('Failed to update round');
    } finally {
      setSaving(false);
    }
  };

  const handleShortlist = async (roundNumber, threshold) => {
    if (!window.confirm(`This will shortlist teams with scores >= ${threshold}% and eliminate others. Continue?`)) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/rounds/${roundNumber}/shortlist?threshold=${threshold}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.shortlisted} teams shortlisted, ${response.data.eliminated} eliminated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Shortlisting failed');
    }
  };

  const getRoundIcon = (roundNumber) => {
    switch (roundNumber) {
      case 1: return <Clock className="text-blue-600" size={24} />;
      case 2: return <Users className="text-orange-600" size={24} />;
      case 3: return <Trophy className="text-purple-600" size={24} />;
      default: return <Clock size={24} />;
    }
  };

  const getRoundColor = (roundNumber) => {
    switch (roundNumber) {
      case 1: return 'border-blue-200 bg-blue-50';
      case 2: return 'border-orange-200 bg-orange-50';
      case 3: return 'border-purple-200 bg-purple-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div></div>;
  }

  const rounds = config?.rounds || {};
  const currentRound = config?.current_round || 0;

  return (
    <div className="space-y-6" data-testid="round-config-section">
      {/* Header with View Toggle */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Round Configuration</h2>
          <p className="text-sm text-gray-500">Manage submission rounds, deadlines, and form fields</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('config')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'config'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="view-config-btn"
            >
              <Settings size={16} />
              <span className="hidden sm:inline">Round Settings</span>
            </button>
            <button
              onClick={() => setActiveView('fields')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'fields'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="view-fields-btn"
            >
              <FileText size={16} />
              <span className="hidden sm:inline">Form Fields</span>
            </button>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-lg">
            <span className="text-sm text-purple-700">Current Round:</span>
            <span className="font-bold text-purple-700">
              {currentRound === 0 ? 'Not Started' : `Round ${currentRound}`}
            </span>
          </div>
        </div>
      </div>

      {/* Dynamic Form Fields Manager View */}
      {activeView === 'fields' && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <AdminRoundFieldsManager token={token} />
        </div>
      )}

      {/* Round Configuration View */}
      {activeView === 'config' && (
        <>
          {/* Round Timeline */}
          <div className="flex items-center justify-center gap-2 py-4">
            {[1, 2, 3].map((num, idx) => (
              <React.Fragment key={num}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  currentRound >= num 
                    ? 'bg-green-500 text-white' 
                    : currentRound === num - 1 
                      ? 'bg-purple-600 text-white animate-pulse' 
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentRound > num ? <CheckCircle size={20} /> : num}
                </div>
                {idx < 2 && (
                  <ArrowRight className={`${currentRound > num ? 'text-green-500' : 'text-gray-300'}`} size={24} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Round Cards */}
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map(roundNum => {
              const round = rounds[roundNum.toString()] || {};
              const isActive = round.is_active;
              const isCurrent = currentRound === roundNum;

              return (
                <div 
                  key={roundNum}
                  className={`rounded-xl border-2 p-6 transition-all ${
                    isCurrent ? 'ring-2 ring-purple-500 ' : ''
                  }${getRoundColor(roundNum)}`}
                  data-testid={`round-${roundNum}-card`}
                >
                  {/* Round Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getRoundIcon(roundNum)}
                      <div>
                        <h3 className="font-bold text-gray-900">Round {roundNum}</h3>
                        <p className="text-xs text-gray-500">{round.name}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isActive ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Round Description */}
                  <p className="text-sm text-gray-600 mb-4">{round.description}</p>

                  {/* Dates */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                      <input
                        type="datetime-local"
                        value={round.start_date ? round.start_date.slice(0, 16) : ''}
                        onChange={(e) => {
                          const newRounds = { ...rounds };
                          newRounds[roundNum.toString()] = { ...round, start_date: e.target.value };
                          setConfig({ ...config, rounds: newRounds });
                        }}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        data-testid={`round-${roundNum}-start-date`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                      <input
                        type="datetime-local"
                        value={round.end_date ? round.end_date.slice(0, 16) : ''}
                        onChange={(e) => {
                          const newRounds = { ...rounds };
                          newRounds[roundNum.toString()] = { ...round, end_date: e.target.value };
                          setConfig({ ...config, rounds: newRounds });
                        }}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        data-testid={`round-${roundNum}-end-date`}
                      />
                    </div>
                  </div>

                  {/* Submissions Toggle - allow for all rounds */}
                  <div className="flex items-center justify-between py-2 border-t border-b mb-4">
                    <span className="text-sm text-gray-600">Allow Submissions</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={round.allow_submissions || false}
                        onChange={(e) => {
                          const newRounds = { ...rounds };
                          newRounds[roundNum.toString()] = { ...round, allow_submissions: e.target.checked };
                          setConfig({ ...config, rounds: newRounds });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => updateRound(roundNum, {
                        is_active: !isActive,
                        allow_submissions: round.allow_submissions || false,
                        start_date: round.start_date,
                        end_date: round.end_date
                      })}
                      disabled={saving}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      data-testid={`toggle-round-${roundNum}`}
                    >
                      {isActive ? <Pause size={16} /> : <Play size={16} />}
                      {isActive ? 'Deactivate Round' : 'Activate Round'}
                    </button>

                    {roundNum < 3 && (
                      <button
                        onClick={() => handleShortlist(roundNum, 50)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-200"
                        data-testid={`shortlist-round-${roundNum}`}
                      >
                        <Users size={16} />
                        Shortlist for Round {roundNum + 1}
                      </button>
                    )}

                    <button
                      onClick={() => updateRound(roundNum, {
                        is_active: round.is_active || false,
                        allow_submissions: round.allow_submissions || false,
                        start_date: round.start_date,
                        end_date: round.end_date
                      })}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                      data-testid={`save-round-${roundNum}`}
                    >
                      <Save size={16} />
                      Save Changes
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">How Rounds Work</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Round 1:</strong> Teams submit basic details, expertise, and tech stack</li>
              <li>• <strong>Round 2:</strong> Technical submission with GitHub repo and video</li>
              <li>• <strong>Round 3:</strong> Final submission for evaluation</li>
            </ul>
          </div>

          {/* Tip for Form Fields */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-800 mb-2">Customizing Submission Forms</h4>
            <p className="text-sm text-amber-700">
              Click on <strong>"Form Fields"</strong> above to add custom fields to each round's submission form. 
              Changes will be immediately reflected in team dashboards.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRoundConfigSection;
